/**
 * Microphone acquisition and permission state.
 *
 * This module is deliberately the only place in the client that calls `getUserMedia`.
 * It owns the stream; the recogniser borrows it. `recognizerLive.stop()` therefore
 * disconnects its audio graph but never stops the tracks — that is `release()`'s job,
 * so that pausing recognition does not make the iOS "microphone in use" indicator
 * flicker and does not risk losing the permission grant mid-session.
 *
 * ## The constraints matter more than anything else here
 *
 * `echoCancellation`, `noiseSuppression` and `autoGainControl` are all tuned for a
 * single human voice. On a piano they are actively destructive:
 *
 *  - **AGC** rides the level, so the energy gate and the spectral flux onset detector
 *    both see a signal whose loudness has been flattened. Attacks stop looking like
 *    attacks and decays stop looking like decays.
 *  - **Noise suppression** is a spectral subtraction tuned to remove stationary,
 *    non-speech content. A held chord *is* stationary non-speech content; it gets
 *    treated as noise and thinned out, which is exactly the material chroma needs.
 *  - **Echo cancellation** engages an adaptive filter against the output path and
 *    introduces both non-linear distortion and a variable delay.
 *
 * So they are all requested `false`. If the platform refuses the constraint set
 * outright we retry with a bare `{ audio: true }` rather than failing to listen at
 * all, but the resulting stream is flagged `degraded` and the tuning screen says so
 * in as many words — recognition results gathered on a degraded stream are not
 * comparable with results gathered on a clean one.
 */

import type { SessionState } from '@hc/shared';

/** Matches `SessionState['micStatus']` exactly, so the store can assign it directly. */
export type MicStatus = SessionState['micStatus'];

export type MicErrorKind =
  /** The user (or a policy) said no. Recoverable only by the user changing a setting. */
  | 'denied'
  /** No input device, or the device is held by something else. */
  | 'unavailable'
  /** `getUserMedia` is not exposed at all — almost always a non-HTTPS origin. */
  | 'insecure-context'
  | 'unknown';

export interface MicError {
  kind: MicErrorKind;
  /** The underlying `DOMException.name`, or `''` when we synthesised the error. */
  name: string;
  /** Safe to show a user. */
  message: string;
}

export interface MicAcquisition {
  status: MicStatus;
  stream: MediaStream | null;
  error: MicError | null;
  /**
   * True when the browser rejected the "no processing" constraint set and we fell back
   * to `{ audio: true }`. Echo cancellation / noise suppression / AGC are then likely
   * to be ON, and recognition quality will suffer. Surface this to the user.
   */
  degraded: boolean;
}

/** Injection seam. Every default reads from `navigator` / `window` lazily. */
export interface MicManagerDeps {
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  /** Resolves `null` when the Permissions API cannot answer (Safari). */
  queryPermission?: () => Promise<PermissionState | null>;
  isSecureContext?: () => boolean;
}

/**
 * The constraint set we actually want. Exported so tests can assert on it and so the
 * tuning screen can display what was requested next to `track.getSettings()`.
 */
export const PIANO_AUDIO_CONSTRAINTS: MediaStreamConstraints = Object.freeze({
  audio: Object.freeze({
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: 1,
    // Deliberately no `sampleRate`: iOS ignores it, and the recogniser reads the rate
    // back off the AudioContext rather than assuming one. Constraining it here would
    // only produce spurious OverconstrainedErrors.
  }) as MediaTrackConstraints,
}) as MediaStreamConstraints;

/** The bare fallback, used only when the set above is rejected outright. */
const FALLBACK_AUDIO_CONSTRAINTS: MediaStreamConstraints = { audio: true };

const INSECURE_CONTEXT_MESSAGE =
  'The microphone needs a secure origin. Open the app over HTTPS (see "Testing on the iPad" ' +
  'in the README for the mkcert flow) or use http://localhost.';

export interface MicManager {
  getStatus(): MicStatus;
  getStream(): MediaStream | null;
  getLastError(): MicError | null;
  /** True when the active stream came from the degraded fallback constraint set. */
  isDegraded(): boolean;
  /**
   * Ask for the microphone. Idempotent while a live stream is held. MUST be called
   * from a user gesture on iOS — Safari will reject it otherwise, and the rejection
   * looks identical to a denial.
   */
  request(): Promise<MicAcquisition>;
  /** Ask the Permissions API without prompting. Resolves the current status. */
  probe(): Promise<MicStatus>;
  /** Stop the tracks and drop the stream. The next `request()` may prompt again. */
  release(): void;
  onStatusChange(listener: (status: MicStatus) => void): () => void;
  /**
   * Fires when the OS takes the input away — a phone call, Siri, another app on iOS.
   * The stream is dead at this point and `request()` must be called again, from a
   * fresh user gesture.
   */
  onStreamEnded(listener: () => void): () => void;
  /** What `track.getSettings()` reports, for the tuning screen. `null` when not open. */
  getTrackSettings(): MediaTrackSettings | null;
}

function defaultGetUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
  const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  if (!devices || typeof devices.getUserMedia !== 'function') {
    return Promise.reject(
      Object.assign(new Error(INSECURE_CONTEXT_MESSAGE), { name: 'NotSupportedError' }),
    );
  }
  return devices.getUserMedia(constraints);
}

async function defaultQueryPermission(): Promise<PermissionState | null> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return null;
  try {
    // `'microphone'` is not in the TS PermissionName union in every lib version, and
    // Safari throws TypeError for unknown names rather than rejecting. Both are caught.
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state;
  } catch {
    return null;
  }
}

function defaultIsSecureContext(): boolean {
  if (typeof window === 'undefined') return true;
  return window.isSecureContext !== false;
}

/** Map a `getUserMedia` rejection onto our four-state status plus a readable reason. */
export function classifyMicError(error: unknown): MicError {
  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? String((error as { name: unknown }).name)
      : '';
  const raw =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return {
        kind: 'denied',
        name,
        message:
          'Microphone access was refused. On iPad: Settings → Safari → Microphone, or the ' +
          '"aA" menu in the address bar → Website Settings.',
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return { kind: 'unavailable', name, message: 'No microphone was found on this device.' };
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        kind: 'unavailable',
        name,
        message: 'The microphone is busy — another app or tab is holding it.',
      };
    case 'NotSupportedError':
      return { kind: 'insecure-context', name, message: INSECURE_CONTEXT_MESSAGE };
    default:
      return { kind: 'unknown', name, message: raw || 'The microphone could not be opened.' };
  }
}

/** Which rejections are worth retrying with the bare `{ audio: true }` constraint set. */
function isConstraintRejection(error: unknown): boolean {
  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? String((error as { name: unknown }).name)
      : '';
  return name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError';
}

export function createMicManager(deps: MicManagerDeps = {}): MicManager {
  const getUserMedia = deps.getUserMedia ?? defaultGetUserMedia;
  const queryPermission = deps.queryPermission ?? defaultQueryPermission;
  const isSecureContext = deps.isSecureContext ?? defaultIsSecureContext;

  let status: MicStatus = 'unrequested';
  let stream: MediaStream | null = null;
  let lastError: MicError | null = null;
  let degraded = false;
  let inFlight: Promise<MicAcquisition> | null = null;

  const statusListeners = new Set<(s: MicStatus) => void>();
  const endedListeners = new Set<() => void>();

  function setStatus(next: MicStatus): void {
    if (status === next) return;
    status = next;
    for (const listener of statusListeners) listener(next);
  }

  function watchTracks(target: MediaStream): void {
    for (const track of target.getAudioTracks()) {
      track.addEventListener('ended', () => {
        // Only react if this is still the stream we think we are holding.
        if (stream !== target) return;
        stream = null;
        degraded = false;
        for (const listener of endedListeners) listener();
      });
    }
  }

  async function acquire(): Promise<MicAcquisition> {
    if (stream && stream.getAudioTracks().some((t) => t.readyState === 'live')) {
      return { status, stream, error: null, degraded };
    }

    if (!isSecureContext()) {
      lastError = { kind: 'insecure-context', name: '', message: INSECURE_CONTEXT_MESSAGE };
      setStatus('unavailable');
      return { status, stream: null, error: lastError, degraded: false };
    }

    let acquired: MediaStream;
    let usedFallback = false;
    try {
      acquired = await getUserMedia(PIANO_AUDIO_CONSTRAINTS);
    } catch (first) {
      if (!isConstraintRejection(first)) {
        lastError = classifyMicError(first);
        setStatus(lastError.kind === 'denied' ? 'denied' : 'unavailable');
        return { status, stream: null, error: lastError, degraded: false };
      }
      try {
        acquired = await getUserMedia(FALLBACK_AUDIO_CONSTRAINTS);
        usedFallback = true;
      } catch (second) {
        lastError = classifyMicError(second);
        setStatus(lastError.kind === 'denied' ? 'denied' : 'unavailable');
        return { status, stream: null, error: lastError, degraded: false };
      }
    }

    stream = acquired;
    degraded = usedFallback;
    lastError = null;
    watchTracks(acquired);
    setStatus('granted');
    return { status, stream, error: null, degraded };
  }

  return {
    getStatus: () => status,
    getStream: () => stream,
    getLastError: () => lastError,
    isDegraded: () => degraded,

    request() {
      if (!inFlight) {
        inFlight = acquire().finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },

    async probe() {
      const state = await queryPermission();
      if (state === 'granted' && status === 'unrequested') setStatus('granted');
      else if (state === 'denied') setStatus('denied');
      // `'prompt'` and `null` both leave the status alone: `null` means we simply do
      // not know (Safari), and claiming 'unrequested' would overwrite a real grant.
      return status;
    },

    release() {
      if (stream) {
        for (const track of stream.getAudioTracks()) track.stop();
      }
      stream = null;
      degraded = false;
    },

    onStatusChange(listener) {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener) as unknown as void;
    },

    onStreamEnded(listener) {
      endedListeners.add(listener);
      return () => endedListeners.delete(listener) as unknown as void;
    },

    getTrackSettings() {
      const track = stream?.getAudioTracks()[0];
      if (!track || typeof track.getSettings !== 'function') return null;
      return track.getSettings();
    },
  };
}
